/**
 * @module experience/change-plan-services
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/*tslint:disable max-file-line-count */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import { of as observableOf, throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
// Service
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { BrowseServicesService } from '@experience/shared-experience/services/browse-service.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
// Interface
import { ChangePlanAEMContent } from '@core/aem/interfaces/change-plan-services/change-plan.interface';
import { ChangeServicesAEMContent } from '@core/aem/interfaces/change-plan-services/change-services.interface';
import { PendingOrdersConfigData } from '@core/aem/interfaces/change-plan-services/pending-orders.interface';
import { SessionPassConfigData } from '@core/aem/interfaces/change-plan-services/session-pass.interface';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import {
  CustomerSessionPassDetails,
  CustomerSessionPassDetailsLines,
} from '@support/api/proxies/responses/api-2010.interface';
import {
  Cart,
  CartLine,
  CartItem,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { CompleteServicesInterface } from '@experience/shop/shared-browse/interfaces/service-blade.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { SetEffectiveDateConfigData } from '@core/aem/interfaces/set-effective-date/set-effective-date.interface';
import { SessionPass } from '@experience/change-plan-services/interface/on-demand-session-pass.interface';
import { SessionPassSetEffectiveDateConfigData } from '@core/aem/interfaces/change-plan-services/session-pass-set-effecive-date.interface';
import { OnDemandSessionPassInterface } from '@experience/change-plan-services/interface/on-demand-session-pass.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { LineDetails } from '@support/api/proxies/responses/api-2007.interface';
import {
  CustomerLineBillingDetails,
  CustomerLineBillingDetailsAccountLines,
} from '@support/api/proxies/responses/api-2008.interface';
import {
  CustomerAccountBillingDetails,
  LineSummary,
} from '@support/api/proxies/responses/api-2006.interface';
import { APIError } from '@support/api/common/error.interface';
import { AddSessionPassResponse } from '@support/api/proxies/responses/api-2147.interface';
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';
import {
  OrderSearchResponse,
  OrderSearchResults,
} from '@support/api/proxies/responses/api-2813.interface';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
@Injectable({
  providedIn: 'root',
})
export class ChangePlansService {
  /**
   * Holds the value of Change Plan Services Config Data as a Subject
   */
  public configurableContentSubject: Subject<ChangePlanAEMContent | ChangeServicesAEMContent>;
  /**
   * Holds the value of Change Plan Services Config Data as an Observable
   */
  public configurableContent$: Observable<ChangePlanAEMContent | ChangeServicesAEMContent>;
  /**
   * Subject for the planOption
   */
  public selectedPlanSubject: Subject<PlanOption>;
  /**
   * Holds the value of selected Planbas an Observable
   */
  public selectedPlan$: Observable<PlanOption>;
  /**
   * Subject for the selected Plan
   */
  public onSetEffectiveDatePageSubject: Subject<boolean>;
  /**
   * Subject for the set effective date page
   */
  public onSetEffectiveDatePage$: Observable<boolean>;
  /**
   * Subject for the selected Plan
   */
  public onPendingOrderPageSubject: Subject<boolean>;
  /**
   * Subject for the set effective date page
   */
  public onPendingOrderPage$: Observable<boolean>;
  /**
   * Subject to update when we have a new line in focus
   */
  public lineIdInFocusUpdatedSubject: Subject<boolean>;
  /**
   * Observable to detect when there is a change in line in focus
   */
  public lineIdInFocusUpdated$: Observable<boolean>;
  /**
   * Holds the value of selected Plan Id
   */
  public selectedPlan: string;
  /**
   * holds the new plan selected
   */
  public newPlan: PlanOption;
  /**
   * Holds saved category
   */
  public selectedCategory: string;
  /**
   * Session Pass Details Data set
   */
  public sessionPassDetails: OnDemandSessionPassInterface;
  /**
   * Variable to hold pending order count
   */
  public pendingOrderCount: number;
  /**
   * Holds the value of Change Plan Services Config Data as a Subject
   */
  public pendingOrderSubject: Subject<boolean>;
  /**
   * Holds the value of Change Plan Services Config Data as an Observable
   */
  public pendingOrder$: Observable<boolean>;
  /**
   * Variable to hold back url for CPS flow
   */
  private backUrl: string;
  /**
   * Unsubscribes from all the subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private contentProviderService: ContentProviderService,
    private stateStoreUtilService: StateStoreUtilsService,
    private browseServicesService: BrowseServicesService,
    private userService: UserService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGatewayService: ApiGatewayService,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService
  ) {
    this.configurableContentSubject = new Subject();
    this.configurableContent$ = this.configurableContentSubject.asObservable();
    this.pendingOrderSubject = new Subject();
    this.pendingOrder$ = this.pendingOrderSubject.asObservable();
    this.selectedPlanSubject = new Subject();
    this.ngUnsubscribe = new Subject<void>();
    this.selectedPlan$ = this.selectedPlanSubject.asObservable();
    this.onSetEffectiveDatePageSubject = new Subject();
    this.onSetEffectiveDatePage$ = this.onSetEffectiveDatePageSubject.asObservable();
    this.onPendingOrderPageSubject = new Subject();
    this.onPendingOrderPage$ = this.onPendingOrderPageSubject.asObservable();
    this.lineIdInFocusUpdatedSubject = new Subject();
    this.lineIdInFocusUpdated$ = this.lineIdInFocusUpdatedSubject.asObservable();
  }
  /**
   * public method to get the change plan service AEM content data from content provider service
   * @returns: Observable<ChangePlanAEMContent>
   */
  public getCPSAEMContent(): Observable<
    | ChangeServicesAEMContent
    | ChangePlanAEMContent
    | PendingOrdersConfigData
    | SetEffectiveDateConfigData
    | SessionPassConfigData
    | SessionPassSetEffectiveDateConfigData
  > {
    return this.contentProviderService.getContentData(null).pipe(
      map((contentData: ConfigData) => {
        const content = this.contentProviderService.getConfigurableData(contentData);
        this.configurableContentSubject.next(content);
        return content;
      })
    );
  }
  /**
   * Method to set entry point for CPS flow
   * @returns void
   */
  public setEntryPoint(): void {
    this.backUrl = this.stateStoreUtilService.getCPSEntryPoint();
  }
  /**
   * Method to get entry point for CPS flow
   */
  public getEntryPoint(): string {
    return this.backUrl;
  }
  /**
   * public method to emit the selected plan
   * @param planOption
   * @returns: void
   */
  public emitSelectedPlan(plan: PlanOption): void {
    if (!this.stateStoreUtilService.getActivePlanForLine()) {
      this.stateStoreUtilService.setActivePlanForLine(plan.planOptionSelectorId);
      this.stateStoreUtilService.setPlansPDPSelectedPlanId(plan.planOptionSelectorId);
    }
    this.selectedPlan = plan.planOptionSelectorId;
    this.newPlan = plan;
    this.selectedPlanSubject.next(plan);
  }
  /**
   * public method to check if plan is changed
   * @returns: boolean
   */
  public isPlanChanged(): boolean {
    return this.stateStoreUtilService.getActivePlanForLine() !== this.selectedPlan;
  }

  /**
   * Method to check if Cart is created or not
   * @returns boolean
   */
  public isCartCreated(): boolean {
    const cart = this.stateStoreUtilService.getCart();
    return this.objectService.get(cart, 'cartId') ? true : false;
  }
  /**
   * This method is used to set new plan for line
   * @returns void
   */
  public setNewPlan(): void {
    // @TODO add dynamic integration of the cartId
    if (this.stateStoreUtilService.getActivePlanForLine() !== this.newPlan.planOptionSelectorId) {
      this.stateStoreUtilService.setNewPlanForLine(this.newPlan);
    }
  }
  /**
   * This method is used to clear out new plan from cache if available
   * @returns void
   */
  public clearNewPlanFromCache(): void {
    if (this.stateStoreUtilService.getNewPlanForLine()) {
      this.stateStoreUtilService.clearNewPlanForLine();
    }
  }
  /**
   * Method to get pending order count
   * @returns {string}
   */
  public getPendingOrderCount(): string {
    const lineDetails = this.stateStoreUtilService.getLineBillingDetails();
    return this.objectService.get(lineDetails, 'account.totalPendingOrderCount');
  }
  /**
   * Method to get list of services
   * @returns Observable<CompleteServicesInterface>
   */
  public getServices(): Observable<CompleteServicesInterface> {
    return this.browseServicesService.getServiceData(true);
  }
  /**
   * Get Session Pass Details for selected line
   * @return void
   */
  public getLineSessionPassDetails(): Observable<CustomerSessionPassDetails> {
    return this.userService.customerAccountLineSessionPassDetails();
  }
  /**
   * Method to set demand pass value and add
   * @returns void
   * @param {SessionPass} demandPass
   */
  public setDemandPass(
    sessionPassDetails: OnDemandSessionPassInterface,
    demandPass: SessionPass,
    selectedCategory: string,
    overrideIndicator?: boolean
  ): Observable<AddSessionPassResponse> {
    this.sessionPassDetails = sessionPassDetails;
    this.selectedCategory = selectedCategory;
    const requestPayload = {
      transactionTypes: [CPS_CONSTANTS.MANAGEADDON],
      sessionPassId: demandPass.id,
      lineOfServiceId: this.stateStoreUtilService.getLineId(),
      quantity: demandPass.sessionPassCount,
    };
    let header = new HttpHeaders();
    if (overrideIndicator) {
      header = header.append('overrideIndicator', overrideIndicator.toString());
    }
    const pathParam = {
      cartId: this.stateStoreUtilService.getCartId()
        ? this.stateStoreUtilService.getCartId()
        : CPS_CONSTANTS.CARTID,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2147_addSessionPass, requestPayload, header, null, pathParam)
      .pipe(
        map((response: HttpResponse<AddSessionPassResponse>) => {
          const resp: AddSessionPassResponse = response.body;
          const cart: Cart = this.objectService.get(resp, 'cart');
          this.stateStoreUtilsService.setCartId(this.objectService.get(cart, 'cartId'));
          this.stateStoreUtilsService.setCart(cart);
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * To post the service Bundle into Cart
   * @param id : Service Bundle Id
   * @param lineId : Current line id
   * @returns Observable<Cart>
   */
  public postServiceBundle(
    id: string,
    lineId: string,
    overrideIndicator?: boolean
  ): Observable<Cart> {
    return this.cartSupportService.postServiceBundle(id, lineId, overrideIndicator, true);
  }
  /**
   * Method to save updated Cart details into cart and annouce its updation
   * @param data : updated cart response
   * returns void
   */
  public setCartInfo(data: Cart): void {
    this.stateStoreUtilService.setCartId(data.cartId);
    this.cartSupportService.announceCartUpdate(data);
  }
  /**
   * Method to update the current page
   * @returns void
   */
  public updateCurrentPage(setCurrentPage: boolean): void {
    this.onSetEffectiveDatePageSubject.next(setCurrentPage);
    this.onPendingOrderPageSubject.next(setCurrentPage);
  }
  /**
   * Method to get selected line session pass details
   * @param customerSessionPassDetails of type CustomerSessionPassDetails
   * @param activeLineId of type string
   * @returns CustomerSessionPassDetailsLines having selected line session pass details
   */
  public getSelectedLineSessionPassDetails(
    customerSessionPassDetails: CustomerSessionPassDetails,
    activeLineId: string
  ): CustomerSessionPassDetailsLines {
    if (customerSessionPassDetails) {
      const passLines = this.objectService.get(customerSessionPassDetails, 'lines');
      if (passLines) {
        return passLines.find((item: CustomerSessionPassDetailsLines) => {
          return item.lineOfServiceId === activeLineId;
        });
      }
    }
  }

  /**
   * Method to get selected line info
   * @param customerAccountInfo of type CustomerDetailsResponse
   * @param activeLineId of type string
   * @returns LineDetails having selected line information
   */
  public getLineInfo(
    customerLineInfo: CustomerAccountBillingDetails,
    activeLineId: string
  ): LineDetails {
    if (customerLineInfo) {
      return customerLineInfo.lines.find((item: LineSummary) => {
        return item.lineOfServiceId === activeLineId;
      });
    }
  }

  /**
   * Method to get selected line billing details
   * @param getLineBillingDetails of type CustomerLineBillingDetails
   * @param activeLineId of type string
   * @returns CustomerLineBillingDetailsAccountLines having selected line billing details
   */
  public getLineBillingDetails(
    customerLineBillingDetails: CustomerLineBillingDetails,
    activeLineId: string
  ): CustomerLineBillingDetailsAccountLines {
    if (customerLineBillingDetails) {
      const lines = this.objectService.get(customerLineBillingDetails, 'account.lines');
      if (lines) {
        return lines.find((item: CustomerLineBillingDetailsAccountLines) => {
          return item.lineID === activeLineId;
        });
      }
    }
  }
  /**
   * Method to get Plans AEM content
   * @returns Observable of ChangePlanAEMContent
   */
  public getChangePlanAEMContent(): Observable<ChangePlanAEMContent | SessionPassConfigData> {
    return this.contentProviderService.getContentData(null).pipe(
      map((response: ConfigData) => {
        const content = this.contentProviderService.getConfigurableData(response);
        this.configurableContentSubject.next(content);
        return response;
      })
    );
  }
  /**
   * Method to get the pending orders for the respective line od
   * @param lineId Line Id whose corresponding orders are required
   *
   * @returns string
   */
  public getPendingOrder(
    lineId: string,
    msisdn: string,
    flowValue?: string,
    isResponseFromCache?: boolean
  ): Observable<OrderSearchResults[]> {
    const orderResponse = this.stateStoreUtilService.getPendingOrders(lineId);
    if (
      this.stateStoreUtilsService.getCPSEntryPoint() === SHARED_CONSTANTS.LINE_LANDING ||
      isResponseFromCache ||
      orderResponse
    ) {
      if (orderResponse) {
        return observableOf(orderResponse);
      }
      return observableOf(null);
    } else {
      const lookUpParams: { [name: string]: string } = {};
      lookUpParams.msisdn = msisdn;
      lookUpParams.flowValue = flowValue;
      return this.userService.getPendingOrderResults(lookUpParams).pipe(
        map(
          (result: OrderSearchResponse) => {
            this.stateStoreUtilService.setPendingOrders(result.orderSearchResults, lineId);
            return result.orderSearchResults;
          },
          (error: APIError) => {
            return error;
          }
        )
      );
    }
  }

  /**
   * Method to check if new plan was not added successfully in cart
   * @returns boolean
   */
  public isPlanRemoved(): boolean {
    let isPlanRemoved;
    const cartResponse = this.stateStoreUtilsService.getCompleteCartResponse();
    const selectedLine = cartResponse.cart.lines.find((cartLine: CartLine) => {
      return cartLine.id === this.stateStoreUtilsService.getLineId();
    });
    if (selectedLine) {
      isPlanRemoved = selectedLine.plan.filter((cartItem: CartItem) => {
        return (
          cartItem.offerId === this.stateStoreUtilsService.getPlansPDPSelectedPlanId() &&
          cartItem.statusCode === CPS_CONSTANTS.REMOVE
        );
      });
    }
    return isPlanRemoved && cartResponse.status.code === CPS_CONSTANTS.ERROR_101;
  }
}
